import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import { T } from "@/lib/testIds";
import { ArrowRight, BookOpen, CheckCircle2, GraduationCap, Layers, Award, ShieldCheck, ScrollText, CalendarClock } from "lucide-react";

function formatUKDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
  } catch {
    return iso;
  }
}

export default function CourseDetail() {
  const { slug } = useParams();
  const [course, setCourse] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    (async () => {
      const results = await Promise.allSettled([
        api.get(`/courses/${slug}`),
        api.get(`/progress/${slug}`),
      ]);
      if (results[0].status === "fulfilled") setCourse(results[0].value.data);
      if (results[1].status === "fulfilled") setProgress(results[1].value.data);
      else setProgress({ completed_lessons: [], total_lessons: 0, percent: 0, quiz_passed: false });
    })();
  }, [slug]);

  if (!course) return <Layout><div className="text-brand-muted">Loading course…</div></Layout>;

  const completedIds = new Set(progress?.completed_lessons || []);
  const nextLesson = course.lessons.find((l) => !completedIds.has(String(l.order))) || course.lessons[0];

  return (
    <Layout>
      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 min-w-0">
          <div className="text-xs uppercase tracking-[0.28em] text-brand-muted mb-4">{course.level} · {course.duration}</div>
          <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-brand-ink leading-[1.05] [overflow-wrap:anywhere]">{course.title}</h1>
          <p className="mt-5 text-brand-body text-base sm:text-lg leading-relaxed break-words">
            {/* Strip raw markdown bold markers — the description is seeded with
                **bold** fragments for emphasis but this view renders plain text. */}
            {(course.description || "").replace(/\*\*(.+?)\*\*/g, "$1")}
          </p>

          <div className="mt-10 flex flex-wrap gap-3">
            <Link to={`/courses/${slug}/lessons/${nextLesson.order}`} data-testid={T.courseStartLesson}
              className="inline-flex items-center gap-2 bg-brand-ink text-brand-bone rounded-full px-6 py-3 hover:bg-brand-inkHover">
              {completedIds.size > 0 ? "Continue lesson" : "Start first lesson"} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
            </Link>
            <Link to={`/courses/${slug}/flashcards`} data-testid={T.courseFlashcardsBtn}
              className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-6 py-3 hover:bg-brand-linen">
              <Layers className="w-4 h-4" strokeWidth={1.5} /> Flashcards
            </Link>
            <Link to={`/courses/${slug}/quiz`} data-testid={T.courseQuizBtn}
              className="inline-flex items-center gap-2 border border-brand-linen text-brand-ink rounded-full px-6 py-3 hover:bg-brand-linen">
              <GraduationCap className="w-4 h-4" strokeWidth={1.5} /> Final assessment
            </Link>
            {progress?.quiz_passed && (
              <Link to={`/courses/${slug}/certificate`}
                className="inline-flex items-center gap-2 bg-brand-accent text-white rounded-full px-6 py-3 hover:bg-brand-accentHover">
                <Award className="w-4 h-4" strokeWidth={1.5} /> View course certificate
              </Link>
            )}
          </div>

          <h2 className="font-serif text-3xl text-brand-ink mt-16 mb-6">Curriculum</h2>
          <div className="space-y-3">
            {course.lessons.map((l, i) => {
              const done = completedIds.has(String(l.order));
              return (
                <motion.div key={l.order} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.4, delay: i * 0.04 }}>
                  <Link data-testid={T.courseLessonItem} to={`/courses/${slug}/lessons/${l.order}`}
                    className={`flex items-center gap-4 p-5 rounded-2xl border transition-all ${done ? "bg-brand-linen/50 border-brand-linen" : "bg-white border-brand-linen hover:border-brand-ink"}`}>
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-medium ${done ? "bg-brand-ink text-brand-bone" : "bg-brand-bone border border-brand-linen text-brand-secondary"}`}>
                      {done ? <CheckCircle2 className="w-4 h-4" strokeWidth={2} /> : l.order}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-serif text-xl text-brand-ink">{l.title}</div>
                      <div className="text-sm text-brand-muted truncate">{l.summary}</div>
                    </div>
                    <div className="hidden sm:block text-[11px] uppercase tracking-widest text-brand-muted flex-shrink-0" data-testid={`lesson-time-${l.order}`}>
                      {Math.max(1, Math.ceil(((l.content || "").length + (l.summary || "").length) / 1250))} min
                    </div>
                    <ArrowRight className="w-4 h-4 text-brand-muted" strokeWidth={1.5} />
                  </Link>
                </motion.div>
              );
            })}
          </div>

          {course.governance && (
            <section data-testid="governance-card" className="mt-16 bg-brand-bone border border-brand-linen rounded-3xl overflow-hidden">
              <div className="p-6 border-b border-brand-linen flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-brand-ink text-brand-bone flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5" strokeWidth={1.6} />
                </div>
                <div className="min-w-0">
                  <div className="text-xs uppercase tracking-[0.28em] text-brand-muted">Governance & compliance</div>
                  <h3 className="font-serif text-2xl text-brand-ink mt-1">How this module is kept safe and current</h3>
                </div>
              </div>
              <div className="p-6 grid md:grid-cols-2 gap-6">
                {Array.isArray(course.governance.learning_outcomes) && course.governance.learning_outcomes.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-muted flex items-center gap-2">
                      <GraduationCap className="w-3.5 h-3.5" strokeWidth={1.7} /> Learning outcomes
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-brand-body list-disc list-outside pl-5">
                      {course.governance.learning_outcomes.map((lo, idx) => <li key={idx}>{lo}</li>)}
                    </ul>
                  </div>
                )}
                {Array.isArray(course.governance.policy_basis) && course.governance.policy_basis.length > 0 && (
                  <div>
                    <div className="text-xs uppercase tracking-widest text-brand-muted flex items-center gap-2">
                      <ScrollText className="w-3.5 h-3.5" strokeWidth={1.7} /> Policy & legislation
                    </div>
                    <ul className="mt-3 space-y-2 text-sm text-brand-body list-disc list-outside pl-5">
                      {course.governance.policy_basis.map((p, idx) => <li key={idx}>{p}</li>)}
                    </ul>
                  </div>
                )}
              </div>
              <div className="px-6 pb-6 grid sm:grid-cols-3 gap-4 text-sm">
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-brand-muted">Reviewed by</div>
                  <div className="text-brand-ink font-medium mt-1" data-testid="governance-reviewed-by">
                    {course.governance.reviewed_by || "—"}
                  </div>
                  {course.governance.reviewed_by_title && (
                    <div className="text-xs text-brand-muted">{course.governance.reviewed_by_title}</div>
                  )}
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-brand-muted flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" strokeWidth={1.7} /> Last reviewed
                  </div>
                  <div className="text-brand-ink font-medium mt-1" data-testid="governance-reviewed-at">
                    {formatUKDate(course.governance.reviewed_at)}
                  </div>
                </div>
                <div>
                  <div className="text-[11px] uppercase tracking-widest text-brand-muted flex items-center gap-1">
                    <CalendarClock className="w-3 h-3" strokeWidth={1.7} /> Next review due
                  </div>
                  <div className="text-brand-ink font-medium mt-1" data-testid="governance-next-review">
                    {formatUKDate(course.governance.next_review_due)}
                  </div>
                </div>
              </div>
              {course.governance.disclaimer && (
                <div className="px-6 pb-6 pt-2 border-t border-brand-linen mt-2">
                  <div className="text-[11px] uppercase tracking-widest text-brand-muted">Disclaimer</div>
                  <p className="text-xs text-brand-muted mt-2 leading-relaxed" data-testid="governance-disclaimer">{course.governance.disclaimer}</p>
                </div>
              )}
            </section>
          )}
        </div>

        <aside className="lg:col-span-1">
          <div className="sticky top-24 space-y-6">
            <div className="bg-white border border-brand-linen rounded-3xl overflow-hidden shadow-card">
              <img src={course.thumbnail} alt={`${course.title} — course cover`} className="w-full aspect-[4/3] object-cover" />
              <div className="p-6">
                <div className="text-xs uppercase tracking-widest text-brand-muted mb-2">Your progress</div>
                <div className="flex items-baseline gap-2">
                  <div className="font-serif text-5xl text-brand-ink">{progress?.percent ?? 0}%</div>
                  <div className="text-brand-body text-sm">complete</div>
                </div>
                <div className="mt-4 h-1.5 bg-brand-linen rounded-full overflow-hidden">
                  <div className="h-full bg-brand-ink transition-all" style={{ width: `${progress?.percent ?? 0}%` }} />
                </div>
                <div className="mt-6 pt-6 border-t border-brand-linen space-y-3 text-sm">
                  <div className="flex items-center gap-2 text-brand-body">
                    <BookOpen className="w-4 h-4 text-brand-muted" strokeWidth={1.5} />
                    {course.lessons.length} lessons
                  </div>
                  <div className="flex items-center gap-2 text-brand-body">
                    <Layers className="w-4 h-4 text-brand-muted" strokeWidth={1.5} />
                    {course.flashcards?.length || 0} flashcards
                  </div>
                  <div className="flex items-center gap-2 text-brand-body">
                    <GraduationCap className="w-4 h-4 text-brand-muted" strokeWidth={1.5} />
                    {course.quiz?.questions?.length || 0}-question quiz · pass {course.quiz?.pass_score || 70}%
                  </div>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </Layout>
  );
}
